import axios from 'axios';
import { authService } from '@features/auth/services/authService';
import siteConfig from '@config';

interface DecodedVinResponse {
  data: {
    data: {
      components: { vehicle: { make: string; model: string; year: string } };
      metadata: { confidence: number; processingTime: number; schemaVersion: string };
    };
  };
}

/**
 * Per-attempt abort budgets, in order. The decoder lives in a serverless
 * function that loads a vPIC pattern database on its first invocation, so the
 * first call after a deploy or an idle period can take over 10s, against 0.3s
 * warm. A single 10s abort cancels that call before the function answers. The
 * second attempt gets a longer budget because it is the one that inherits a
 * warm function or proves the service is really down.
 *
 * Keep the total under 30s: that is what the E2E helper allows for the whole
 * round trip, and more than that is longer than anyone will sit in front of a
 * disabled VIN field.
 */
export const DECODE_ATTEMPT_TIMEOUTS_MS = [10000, 15000] as const;

/** True for a request WE cancelled, not an answer the service gave us. */
const isTimeout = (err: unknown) => axios.isCancel(err) || (err as Error)?.name === 'AbortError';

const requestDecode = async (vin: string, token: string | undefined, timeoutMs: number) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return (await axios({
      method: 'POST',
      url: `${siteConfig.hopdriveEventsUrl}/.netlify/functions/app-decode-vin`,
      data: {
        vin,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    })) as DecodedVinResponse;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const decodeVin = async (
  vin?: string | null,
  simplified: boolean = false,
  confidenceThreshold: number = 0.6
) => {
  if (!vin) return {};
  const cleansedVin = vin?.toUpperCase()?.trim() || null;

  if (cleansedVin) {
    try {
      const token = await authService.getAccessTokenWithAutoRefresh();

      // Retry only a timeout. An error the service returned (a 400 on an
      // undecodable VIN, a 401) would come back the same.
      let res: DecodedVinResponse | null = null;
      for (let attempt = 0; attempt < DECODE_ATTEMPT_TIMEOUTS_MS.length; attempt += 1) {
        try {
          res = await requestDecode(cleansedVin, token, DECODE_ATTEMPT_TIMEOUTS_MS[attempt]);
          break;
        } catch (err) {
          const last = attempt === DECODE_ATTEMPT_TIMEOUTS_MS.length - 1;
          if (!isTimeout(err) || last) throw err;
          console.warn(
            `VIN decode timed out after ${DECODE_ATTEMPT_TIMEOUTS_MS[attempt]}ms; retrying once`
          );
        }
      }

      // Get the decoded VIN from the response
      const decodedVin = res?.data?.data;
      const vehicle = decodedVin?.components?.vehicle;
      const confidence = decodedVin?.metadata?.confidence;

      // An absent confidence is not a low one. Response shapes with no
      // metadata fall through to the returns below.
      if (confidence !== undefined && confidence < confidenceThreshold) return { vehicle_vin: cleansedVin };

      // Structure the decoded VIN to return what we need for the move
      if (simplified) {
        return {
          vehicle_make: `${vehicle?.make || ``}`,
          vehicle_model: `${vehicle?.model || ``}`,
          vehicle_vin: cleansedVin,
          vehicle_year: `${vehicle?.year || ``}`,
        };
      }

      return decodedVin;
    } catch (err) {
      if (isTimeout(err)) {
        console.error(
          `VIN decode request timed out after ${DECODE_ATTEMPT_TIMEOUTS_MS.length} attempts ` +
            `(${DECODE_ATTEMPT_TIMEOUTS_MS.join('ms, ')}ms)`
        );
      } else {
        console.error('Error decoding VIN:', err);
      }
      return { vehicle_vin: cleansedVin };
    }
  }

  return { vehicle_vin: cleansedVin };
};
